import { useState, useRef, ChangeEventHandler, memo, useMemo, useEffect, MouseEventHandler } from 'react';
import { RcFile } from 'antd/es/upload';
import { TextArea, Image, LottieContainer } from '@/sub-luca-plugin/compatible-components';
import UploadPicture from './upload-picture';
import OcrLabel from './ocr-label';
import { createQaRecordId, toastInQa, uploadLimit } from '@/sub-luca-plugin/utils/qa';
import { createNewQuestion } from '../helper';
import {
    SubjectSearchSvg,
    SendDisabledSvg,
    SendSvg,
    UploadPictureSvg,
    ModalCloseSvg,
} from '@/sub-luca-plugin/compatible-components/svg-icons';
import { eventBusExample } from '@/sub-luca-plugin/utils/class-example';
import { useQaStore } from '@/sub-luca-plugin/store/qa';
import { useNewestData } from '@/sub-luca-plugin/utils/custom-hooks';
import { IMode } from './index.helper';
import { INoticeData } from '@/sub-luca-plugin/types';
import { isWeApp, previewPicture } from '@/sub-luca-plugin/utils';
import classNames from 'classnames';
import './index.scss';
import { tCommon, getCurrentLanguage } from '@/sub-luca-plugin/i18n';

const DialogueTool = () => {
    const { qaConfig, pushQaRecords, validateCanSend, updateQaConfig, interruptAllExcute } = useQaStore();
    const {
        questionState = 0,
        lucaQuestionAgainNum = 0,
        teacherQuestionAgainNum = 0,
        appraiseState,
        transferLaborState,
        aiTag,
        lucaAskChatId,
        askId,
        showSearchQuestionsByOcrBtn,
        showCreateNewQaBtn,
        resourceType,
        isLucaAsk,
        subjectSearchOnOff,
        showSubjectSearchLabel,
    } = qaConfig;
    // 用户正在使用输入法
    const isComposing = useRef(false);
    const preventHideTextArea = useRef(false);
    const textAreaRef = useRef<HTMLTextAreaElement | null | any>(null);
    const blurTimer = useRef<ReturnType<typeof setTimeout>>();
    const pictureUploading = useRef(false);
    const [showTextArea, setShowTextArea] = useState(false);
    const [showStop, setShowStop] = useState(false);
    const [showContinue, setShowContinue] = useState(false);
    const [images, setImages] = useState<string[]>([]);
    const [value, setValue] = useState('');
    const [mode, setMode] = useState<IMode>('normal');

    const newestShowTextArea = useNewestData<boolean>(showTextArea);

    const lang = getCurrentLanguage();
    const isOversea = lang === 'en';

    const reset = () => {
        preventHideTextArea.current = false;
        pictureUploading.current = false;
        setValue('');
        setImages([]);
        setShowStop(false);
        setShowContinue(false);
        setShowTextArea(false);
        setMode('normal');
    };

    // 判断是否需要阻止收起多行文本输入框的逻辑
    const handlePreventHideTextArea = () => {
        if (isWeApp) {
            // 由于小程序中的点击事件是先于输入框失焦事件发生的，所以这里需要一个延时器将点击事件延后发生
            setTimeout(() => clearTimeout(blurTimer.current), 50);
        } else {
            clearTimeout(blurTimer.current);
        }
    };

    // 多行文本框聚焦
    const handleFocus = () => {
        if (newestShowTextArea.current) {
            handlePreventHideTextArea();
            textAreaRef.current.focus();
        } else {
            setShowTextArea(true);
            setTimeout(() => textAreaRef.current.focus(), 1);
        }
    };

    const handleBlur = () => {
        clearTimeout(blurTimer.current);
        blurTimer.current = setTimeout(
            () => {
                if (!value) setShowTextArea(false);
            },
            isWeApp ? 100 : 500
        );
    };

    const handleTextAreaChange: ChangeEventHandler<{ value: string }> = ev => {
        const content = ev.target.value;
        setValue(content);
    };

    const handleDeletePicture = (url: string) => {
        setImages(item => item.filter(item => item !== url));
    };

    // "非AI答疑"或"名师答疑"上传图片后的回调
    const handleUploadChange = (urls: string[]) => {
        pictureUploading.current = false;
        setImages(urls);
        handleFocus();
    };

    const handleBeforeUpload = (file: RcFile) => {
        pictureUploading.current = true;
        return uploadLimit(file);
    };

    const handleClickUpload: MouseEventHandler = event => {
        validateCanSend(['replying', 'ocrAndGptReplying']) ? handlePreventHideTextArea() : event.stopPropagation();
    };

    // 选择模式：学科模型
    const handleSelectMode = (type?: 'subject_search') => {
        setMode(type || 'normal');
        if (type === 'subject_search') handleFocus();
    };

    const handleContinue = () => {
        if (!validateCanSend(['replying', 'ocrAndGptReplying'])) return;
        setShowContinue(false);
        updateQaConfig({ showWhetherSolve: false });
        pushQaRecords(
            {
                recordId: createQaRecordId('questioner'),
                role: 'questioner',
                type: 'basic',
                content: tCommon.t('continuePrompt', '请继续'),
            },
            { filterRecordType: { retry: true, whetherSolve: true, goToTeacherQaTips: true, goToTeacherQa: true, guessYouAsk: true } }
        );
        eventBusExample.publish('DialogueArea', { type: 'continue', params: { content: tCommon.t('continuePrompt', '请继续') } });
    };

    const handleStop = () => eventBusExample.publish('DialogueArea', { type: 'stop' });

    const handleSend = (ev: any) => {
        if (ev.shiftKey || isComposing.current || !validateCanSend(['replying', 'ocrAndGptReplying']) || !value.trim()) return;
        ev?.preventDefault();
        if (pictureUploading.current) {
            toastInQa(tCommon.t('uploadInProgress', '图片正在上传中，请稍后'));
            return;
        }
        // 取消创建大家都想问、相似问题、猜你想问卡片的请求
        interruptAllExcute('send_cancel');
        const recordId = createQaRecordId('questioner');
        pushQaRecords(
            { recordId, role: 'questioner', type: 'basic', content: value, pictures: images },
            {
                // 发送新消息, 清除 请重试、是否解决、可转名师提示、可转名师卡片、转名师权益不足（未成功转名师情况下）等记录
                filterRecordType: {
                    retry: true,
                    whetherSolve: true,
                    goToTeacherQaTips: true,
                    goToTeacherQa: true,
                    rechargeTeacherQa: transferLaborState !== 1,
                    guessYouAsk: true,
                },
            }
        );
        if (transferLaborState === 1) {
            updateQaConfig({ questionState: 0 });
            eventBusExample.publish('DialogueArea', {
                type: isLucaAsk === 1 ? 'sendMessageToTeacher' : 'sendMessageToTeacherOfOldSystem',
                params: { recordType: 22, recordId, askId, images, text: { belongType: 0, content: value } },
            });
        } else if (aiTag === 1) {
            updateQaConfig({ showWhetherSolve: false });
            const type = resourceType !== 2 && images.length ? 'ocrAndLlm' : 'llm';
            eventBusExample.publish('DialogueArea', {
                type,
                params: {
                    mode,
                    content: value,
                    images,
                    preventDisableOcrEditOrReplace: type === 'ocrAndLlm',
                    bizType: type === 'ocrAndLlm' ? 1 : undefined,
                },
            });
        } else {
            updateQaConfig({ showWhetherSolve: false });
            eventBusExample.publish('DialogueArea', {
                type: subjectSearchOnOff ? 'subjectSearch' : 'searchSimilarQuestion',
                params: { recordType: subjectSearchOnOff ? 65 : 21, images, text: { belongType: 0, content: value } },
            });
        }
        reset();
    };

    useEffect(() => {
        eventBusExample.subscribe('DialogueTool', (data: INoticeData) => {
            const { type, params = {} } = data;
            const { content, images } = params;
            switch (type) {
                case 'showStop':
                    setShowStop(true);
                    break;
                case 'hideStop':
                    setShowStop(false);
                    break;
                case 'showContinue':
                    setShowContinue(true);
                    break;
                case 'hideContinue':
                    setShowContinue(false);
                    break;
                case 'echo':
                    setValue(content || '');
                    setImages(images || []);
                    setShowTextArea(true);
                    setTimeout(() => {
                        const length = content?.length || 0;
                        textAreaRef.current?.focus();
                        textAreaRef.current?.resizableTextArea?.textArea?.setSelectionRange(length, length);
                    }, 1);
                    break;
                case 'focus':
                    handleFocus();
                    break;
                default:
                    reset();
                    break;
            }
        });
    }, []);

    const showTool = useMemo(() => {
        if (
            (!transferLaborState && lucaAskChatId && lucaQuestionAgainNum <= 0) ||
            (transferLaborState === 1 && teacherQuestionAgainNum <= 0) ||
            questionState === 2 ||
            appraiseState === 1
        ) {
            return false;
        }
        return true;
    }, [qaConfig]);

    const Operation = useMemo(() => {
        // ocr显示逻辑：全局配置显示 且 当前模式不为学科搜索 且 （资源场景为题目 或 没有上传图片）；
        const ocrLabelVisible = showSearchQuestionsByOcrBtn && mode !== 'subject_search' && (resourceType === 2 || !images.length);
        // 学科搜索显示逻辑： 全局配置显示 且 当前模式不为学科搜索 且 （资源场景为题目 或 没有上传图片）；
        const subjectSearchLabelVisible = showSubjectSearchLabel && mode !== 'subject_search' && (resourceType === 2 || !images.length);

        if (ocrLabelVisible || subjectSearchLabelVisible || showContinue || showStop)
            return (
                <div className="dialogue-tool-stream-controls">
                    {ocrLabelVisible && <OcrLabel />}
                    {subjectSearchLabelVisible && (
                        <div className="dialogue-tool-stream-controls__subject-search" onClick={() => handleSelectMode('subject_search')}>
                            <SubjectSearchSvg className="dialogue-tool-stream-controls__subject-search-icon" />
                            <span>{tCommon.t('searchBySubject', '学科搜索')}</span>
                        </div>
                    )}
                    {(showContinue || showStop) && <div style={{ flex: 1 }} />}
                    {showContinue && (
                        <div className="dialogue-tool-stream-controls__continue" onClick={handleContinue}>
                            {tCommon.t('continueResponse', '继续回答')}
                        </div>
                    )}
                    {showStop && (
                        <div className="dialogue-tool-stream-controls__stop" onClick={handleStop}>
                            {tCommon.t('stopAnswering', '停止回答')}
                        </div>
                    )}
                </div>
            );
        return null;
    }, [showStop, showContinue, showSearchQuestionsByOcrBtn, showSubjectSearchLabel, resourceType, mode, images]);

    const showUpload = useMemo(() => {
        // 非AI 或 转名师 或 题目场景 或 非学科搜索模式
        // 国际化适配: 英文环境下，题目场景 ，不是转人工 的情况不显示上传图片（保持原有逻辑，增加新条件）
        if (aiTag !== 1 || transferLaborState === 1 || (showSearchQuestionsByOcrBtn && mode !== 'subject_search')) {
            return true;
        }

        if (resourceType === 2) {
            return !isOversea || transferLaborState === 1;
        }

        return false;
    }, [aiTag, transferLaborState, resourceType, showSearchQuestionsByOcrBtn, mode, isOversea]);

    const Tool = useMemo(() => {
        return (
            <div className="dialogue-tool">
                {mode === 'subject_search' && (
                    <div className="dialogue-tool__mode">
                        <div className="dialogue-tool__subject-search-label">
                            <SubjectSearchSvg />
                            <span className="dialogue-tool__subject-search-label-text">{tCommon.t('searchBySubject', '学科搜索')}</span>
                        </div>
                        <ModalCloseSvg className="dialogue-tool__mode-close" onClick={() => handleSelectMode(undefined)} />
                    </div>
                )}
                {images.length > 0 && (
                    <div className="dialogue-tool-pictures">
                        {images.map(url => (
                            <div key={url} className="dialogue-tool-picture-contaier">
                                <Image className="dialogue-tool-picture" src={url} onClick={() => previewPicture(url)} />
                                <div className="dialogue-tool-picture-delete" onClick={() => handleDeletePicture(url)} />
                            </div>
                        ))}
                    </div>
                )}
                {showTextArea ? (
                    <div className="dialogue-tool__textarea-container">
                        <TextArea
                            ref={textAreaRef}
                            className="dialogue-tool__textarea"
                            autoSize={{ maxRows: 4 }}
                            value={value}
                            onBlur={handleBlur}
                            onChange={handleTextAreaChange}
                            maxLength={2000}
                            onPressEnter={handleSend}
                            onCompositionStart={() => {
                                isComposing.current = true;
                            }}
                            onCompositionEnd={() => {
                                isComposing.current = false;
                            }}
                        />
                        <div className="dialogue-tool__operations">
                            {showUpload ? (
                                <UploadPicture
                                    maxCount={isWeApp ? 4 : 6}
                                    pictures={images}
                                    beforeUpload={handleBeforeUpload}
                                    onChange={handleUploadChange}
                                >
                                    <UploadPictureSvg className="dialogue-tool__upload-picture" onClick={handleClickUpload} />
                                </UploadPicture>
                            ) : (
                                <div />
                            )}
                            {showStop ? (
                                <LottieContainer type="purpleBallBouncing" excuteType="autoPlayByInit" loop width={40} />
                            ) : value.trim() ? (
                                <SendSvg className="dialogue-tool__send" onClick={handleSend} />
                            ) : (
                                <SendDisabledSvg />
                            )}
                        </div>
                    </div>
                ) : questionState === 4 ? (
                    <div className={classNames('dialogue-tool__input-replacement', 'dialogue-tool__input-replacement--disabled')}>
                        {tCommon.translate({
                            key: 'messageLimitNotice',
                            fallback: `暂不能发送消息（剩余 ${teacherQuestionAgainNum} 次追问）`,
                            variables: { num: teacherQuestionAgainNum },
                        })}
                    </div>
                ) : (
                    <div className="dialogue-tool__input-replacement" onClick={handleFocus}>
                        {questionState === 5
                            ? tCommon.translate({
                                  key: 'remainingFollowups',
                                  fallback: `剩 ${teacherQuestionAgainNum} 次追问`,
                                  variables: { num: teacherQuestionAgainNum },
                              })
                            : lucaAskChatId
                            ? tCommon.translate({
                                  key: 'remainingFollowups',
                                  fallback: `剩 ${lucaQuestionAgainNum} 次追问`,
                                  variables: { num: lucaQuestionAgainNum },
                              })
                            : tCommon.t('enterQuestion', '请输入提问内容～')}
                    </div>
                )}
                {aiTag === 1 && <div className="dialogue-tool__ai-tips">{tCommon.t('aiAccuracyNotice', '内容由AI生成，仅供参考')}</div>}
            </div>
        );
    }, [showTextArea, images, value, showStop, mode, qaConfig, showUpload]);

    const SwitchControl = useMemo(() => {
        if (questionState === 2 || appraiseState === 1) {
            return (
                <>
                    {showCreateNewQaBtn && (
                        <div className="dialogue-tool-switch-controls">
                            <div className="dialogue-tool-switch-controls__new-question" onClick={createNewQuestion}>
                                {tCommon.t('newQuestion', '新问题')}
                            </div>
                        </div>
                    )}
                    {aiTag === 1 && (
                        <div
                            className={classNames(
                                'dialogue-tool-switch-controls__ai-tips',
                                showCreateNewQaBtn && 'dialogue-tool-switch-controls__ai-tips--white-bg'
                            )}
                        >
                            <div>{tCommon.t('aiAccuracyNotice', '内容由AI生成，仅供参考')}</div>
                        </div>
                    )}
                </>
            );
        }
        return null;
    }, [showCreateNewQaBtn, questionState, appraiseState, aiTag]);

    return (
        <>
            {(showTool || showStop) && Operation}
            {showTool && Tool}
            {SwitchControl}
        </>
    );
};

export default memo(DialogueTool);
